import { LinearProgress, Stack, StackProps, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IMainComponent } from 'src/generated-types/main-component'
import { ReactComponent as ClockWarning } from '../../../../assets/svg/clock-warning.svg'
import { ReactComponent as Clock } from '../../../../assets/svg/clock.svg'
import { CertificationValidationPeriod, IMainComponentType } from '../../../../enums'
import { IMainComponentStatus } from '../../../../generated-types/main-component-status'
import { formatDaysToText, GetRGBA } from '../../../../utils'
import { MainComponentItem } from '../../dashboard/mainComponent/MainComponentItem'

interface MainComponentProgressProps extends StackProps {
  cage: IMainComponentStatus
  status: CertificationValidationPeriod
  textColor?: any
  selectedMainComponent?: IMainComponent
  permission?: number
  openEquipmentModal?: boolean
  progressBarHeight?: string
  hideComponentTypes?: IMainComponentType[]
  showExtraDetails?: boolean
  isTooltipDisabled?: boolean
}
export const MainComponentProgress: React.FC<MainComponentProgressProps> = ({
  cage,
  status,
  textColor,
  selectedMainComponent,
  permission,
  openEquipmentModal,
  progressBarHeight,
  hideComponentTypes = [],
  showExtraDetails = false,
  isTooltipDisabled = false,
  ...rest
}) => {
  const { t: translate } = useTranslation(['main-component'])
  const theme = useTheme()

  const iconColor =
    status != CertificationValidationPeriod.NA
      ? status === CertificationValidationPeriod.LOW
        ? theme.palette.errorMain[theme.palette.mode]
        : theme.palette.primaryMain[theme.palette.mode]
      : theme.palette.primaryMain[theme.palette.mode]

  const progressColor =
    status != CertificationValidationPeriod.NA
      ? status === CertificationValidationPeriod.LOW
        ? 'errorMain'
        : 'primaryMain'
      : 'secondaryMain'

  const shouldShowNets = !hideComponentTypes.includes(IMainComponentType.Net)
  const shouldShowRings = !hideComponentTypes.includes(IMainComponentType.Ring)
  const shouldShowLiceSkirts = !hideComponentTypes.includes(IMainComponentType.LiceSkirt)

  const hasNetsWithValidCerts =
    shouldShowNets &&
    cage?.nets &&
    cage.nets.length > 0 &&
    cage.nets.some(
      net => net.certificates && net.certificates.length > 0 && net.certificates.some(cert => cert?.isValid === true),
    )

  const hasRingsWithValidCerts =
    shouldShowRings &&
    cage?.rings &&
    cage.rings.length > 0 &&
    cage.rings.some(
      ring =>
        ring.certificates && ring.certificates.length > 0 && ring.certificates.some(cert => cert?.isValid === true),
    )

  const hasLiceSkirtsWithValidCerts =
    shouldShowLiceSkirts &&
    cage?.liceSkirts &&
    cage.liceSkirts.length > 0 &&
    cage.liceSkirts.some(
      liceSkirt =>
        liceSkirt.certificates &&
        liceSkirt.certificates.length > 0 &&
        liceSkirt.certificates.some(cert => cert?.isValid === true),
    )

  const hasValidCerts = hasNetsWithValidCerts || hasRingsWithValidCerts || hasLiceSkirtsWithValidCerts

  return (
    <Stack width={'100%'} justifyContent={'flex-start'} gap={0.5} {...rest}>
      <LinearProgress
        data-test-id="cage-cert-progress-bar"
        sx={{
          backgroundColor: GetRGBA(theme.palette.black[theme.palette.mode], 0.25),
          height: progressBarHeight,
        }}
        variant="determinate"
        color={progressColor}
        value={hasValidCerts ? cage?.certificateValidationTime ?? 0 : 0}
      />

      <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} columnGap={'8px'} height={'18px'}>
        {hasValidCerts && selectedMainComponent && (
          <>
            <MainComponentItem
              mainComponent={selectedMainComponent}
              type={selectedMainComponent?.type?.componentType}
              removePopOver
              itemTextProps={{ variant: 'body2' }}
              permission={permission}
              openEquipmentModal={openEquipmentModal}
              fontSize={rest.fontSize}
              fontweight={rest.fontWeight}
              showExtraDetails={showExtraDetails}
              isTooltipDisabled={isTooltipDisabled}
            />
            <Typography fontSize={rest.fontSize} color={theme.palette.primaryMain[theme.palette.mode]} paddingX={'2px'}>
              |
            </Typography>
          </>
        )}
        {!hasValidCerts || status != CertificationValidationPeriod.LOW ? (
          <Clock
            color={textColor ? textColor : !hasValidCerts ? theme.palette.primaryMain[theme.palette.mode] : iconColor}
            width={17}
            height={17}
          />
        ) : (
          <ClockWarning color={textColor ? textColor : iconColor} width={17} height={17} />
        )}
        <Typography
          data-test-id="cage-cert-days-text"
          variant="body2"
          fontWeight={rest.fontWeight ?? 500}
          fontSize={rest.fontSize}
          color={
            textColor
              ? textColor
              : !hasValidCerts
              ? theme.palette.primaryMain[theme.palette.mode]
              : status === CertificationValidationPeriod.LOW
              ? theme.palette.errorMain[theme.palette.mode]
              : theme.palette.primaryMain[theme.palette.mode]
          }
        >
          {hasValidCerts
            ? cage?.certificateValidationTime != undefined &&
              cage?.certificateValidationTime >= 0 &&
              cage?.daysLeft != 0
              ? `${formatDaysToText({ days: cage?.daysLeft })} ${translate('left', { ns: 'common' })}`
              : translate('expired', { ns: 'common' })
            : translate('n/a', { ns: 'common' })}
        </Typography>
      </Stack>
    </Stack>
  )
}
